import { useRef, useState } from 'react';
import { DeferredChoiceMenu as ChoiceMenu } from '~/ui/deferred/deferred-menus';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilterTrigger } from './filter-trigger';
import { pick } from './pick';

export type LocationOption = {
  id: string;
  label: string;
};

type LocationFilterProps = {
  value: string[];
  options: LocationOption[];
  onChange: (value: string[]) => void;
  /** Fired when the menu opens, for analytics. */
  onOpen?: () => void;
};

/**
 * Multi-select location filter for the home page search bar. Renders the
 * shared `<FilterTrigger>` (no leading icon) and anchors a Leaf
 * `ChoiceMenu`. The trigger's value shows the count of selected locations,
 * or "All" when nothing is chosen, so the bar stays scannable when
 * collapsed.
 *
 * Selections are committed via `onChange` on EVERY toggle (not on menu close)
 * — the route reflects the change instantly: cards re-filter client-side and
 * the upcoming-events carousel refetches (US-10172). `value` is the committed
 * source of truth (the URL), so the listbox reads straight from it.
 */
export function LocationFilter({
  value,
  options,
  onChange,
  onOpen,
}: LocationFilterProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const selected = pick(options, value);

  return (
    <>
      <FilterTrigger
        ref={triggerRef}
        label={
          <FormattedMessage
            defaultMessage="Location"
            description="Label on the location filter button in the home page search bar"
          />
        }
        value={
          selected.length === 0 || selected.length === options.length ? (
            <FormattedMessage
              defaultMessage="All"
              description="Placeholder on the location filter button when no locations are selected"
            />
          ) : selected.length === 1 ? (
            selected[0]!.label
          ) : (
            <FormattedMessage
              defaultMessage="{count} locations"
              description="Summary on the location filter button when multiple locations are selected"
              values={{ count: selected.length }}
            />
          )
        }
        open={open}
        popupKind="listbox"
        onClick={() => {
          setOpen((wasOpen) => {
            if (!wasOpen) onOpen?.();
            return !wasOpen;
          });
        }}
      />

      <ChoiceMenu
        anchor={triggerRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom start"
        multiple
        value={value}
        onChange={onChange}
        items={options.map((o) => ({ value: o.id, label: o.label }))}
        aria-label={intl.formatMessage({
          defaultMessage: 'Select locations',
          description: 'Aria label for the home page location filter listbox',
        })}
      />
    </>
  );
}
